<template>
  <div class="chartElem">
    <div class="position-right" v-if="historyData && historyData.length > 0">
      <download-excel class="button button-inline"
                      :fields="exportFields"
                      :data="excelData"
                      type="xls"
                      :name="exportFilename"
      >{{ $t('download-csv') }}</download-excel>
    </div>
    <div class="selectors-block">
      <div class="inline"> {{ $t('select-channel') }}:
        <v-select @input="loadHistoryData" class="style-chooser"
                  style="background: white; border-radius: 10px; color: rgb(19,89,103);"
                  label="text"
                  v-model="selectedMedicalDataType"
                  :reduce="data => data.value"
                  :options="availableDataTypes">
        </v-select>
      </div>
      <div class="inline">{{ $t('Select period') }}:
        <v-select @input="loadHistoryData" class="style-chooser"
                  style="background: white; border-radius: 10px; color: rgb(19,89,103);"
                  label="text"
                  v-model="selectedPeriod"
                  :options="availablePeriods">
        </v-select>
      </div>
    </div>

    <div class="chart-block" v-if="chartData.datasets && chartData.datasets[0].data.length > 0 || (selectedMedicalDataType === 'ecg' && ecgData)">
      <ecg-chart :data="ecgData" v-if="selectedMedicalDataType === 'ecg' && ecgData"></ecg-chart>
      <line-chart :chart-data="chartData" :options="chatOptions" v-else></line-chart>
    </div>
    <h2 v-else style="text-align: center">{{$t('No data found')}}</h2>
  </div>
</template>


<script>
import LineChart from "./LineChart";
import EcgChart from "./EcgChart";
import {mapState} from "vuex";
import axios from "axios";
import utils from "../plugins/utils";
import moment from "moment";
export default {
  components: {LineChart, EcgChart},
  props: [],
  data() {
    return {
      selectedMedicalDataType: null,
      selectedPeriod: null,
      ecgData: null,
      availablePeriods: [
        {period: 'lastTest', text: this.$t('Last test')},
        {period: 'hour', text: this.$t('15 minutes'), periodAmount: 0.25},
        {period: 'hour', text: this.$t('1 hour'), periodAmount: 1},
        {period: 'hour', text: this.$t('6 hours'), periodAmount: 6},
        {period: 'day', text: this.$t('24 hours'), periodAmount: 1},
        {period: 'week', text: this.$t('7 days'), periodAmount: 1},
        {period: 'month', text: this.$t('1 month'), periodAmount: 1},
      ],

      chatOptions: {
        scales: {
          yAxes: [
            {
              type: 'linear',
              display: true,
              position: 'right',
              ticks: {
                fontColor: '#eaeaea'
              }
            }
          ],
          xAxes: [
            {
              display: true,
              ticks: {
                fontColor: '#eaeaea',
                callback: function(value, index, values) {
                  return moment(value).format('DD/MM HH:mm');
                }
              }
            }
          ]

        },
        legend: {
          display: false
        },
        maintainAspectRatio: false,
        responsive: true,
        cubicInterpolationMode: 'monotone'
      },

      chartsSettingsByType: {
        hr: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true
        },
        temp: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true
        },
        spo2: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true
        },
        resp: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true
        },
        glucose: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true
        },
        bp_sys: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true
        },
        bp_dia: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true
        },
        avg_rri: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true
        },
        rmssd: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true
        },
        steps: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true
        },
        pose: {
          data: 'data',
          label: 'createdTime',
          isRealTime: true,
          axisType: 'category'
        }
      },

      chartData: {},
      historyData: [],

      exportFields: {
        'Time': 'created',
        'Value': 'data'
      }

    };
  },

  computed: {
    exportFilename() {
      let typeData = this.availableDataTypes.find(type =>type.value === this.selectedMedicalDataType);
      typeData = typeData ? typeData.text : this.selectedMedicalDataType;
      return `FoxiMed History (${this.selectedPatient.name}, ${typeData}).xls`
    },

    excelData() {
      let typeData = this.availableDataTypes.find(type =>type.value === this.selectedMedicalDataType);
      typeData = typeData ? typeData.text : this.selectedMedicalDataType;
      let data = this.historyData.map((row) => {
        return {
          created: this.$options.filters.formatDate(row.created),
          data: row.data
        }
      });

      return [
        {created: `${this.$t('Patient name')}: ${this.selectedPatient.name}
${this.$t('Report generated at')}: ${moment().toString()}
${this.$t('Medical data type')}: ${typeData}
${this.$t('Period')}: ${this.selectedPeriod.text}`},
        {created: ''},
        {created: ''}
      ].concat(data.reverse());
    },

    ...mapState(['selectedPatient']),
    availableDataTypes() {
      return this.$store.state.availableDataTypes;
    },
  },

  methods: {
    loadHistoryData() {
      if (!this.selectedPatient.id) {
        return;
      }
      let requestQuery = {
        type: this.selectedMedicalDataType,
        period: this.selectedPeriod.period,
        periodAmount: this.selectedPeriod.periodAmount,
      }
      if (!this.chartsSettingsByType[this.selectedMedicalDataType] && this.selectedMedicalDataType !== 'ecg') {
        return;
      }
      this.ecgData = null;
      axios.get(`/api/medical/patients/${this.selectedPatient.id}/dataHistory`, {params: requestQuery}).then(res => res.data.data).then(dataHistory => {
        if (this.selectedMedicalDataType === 'ecg') {
          this.ecgData = dataHistory;
          return;
        }
        this.chartData = this.prepareData(dataHistory, this.selectedMedicalDataType);
        this.historyData = dataHistory;
        this.chatOptions.scales.yAxes.forEach((ax) => {
          ax.type = this.chartsSettingsByType[this.selectedMedicalDataType].axisType || 'linear';
          if (ax.type === 'category') {
            ax.labels = this.chartData.datasets[0].data.filter((item, i, ar) => ar.indexOf(item) === i);
          }
        });
      });
    },

    prepareData(data, type) {
      if (this.chartsSettingsByType[type]) {
        return this.prepareChartData(data, this.chartsSettingsByType[type].label, this.chartsSettingsByType[type].data, this.chartsSettingsByType[type], type);
      }
      else {
        return data;
      }
    },

    prepareChartData(rows, labelKey, dataKey, chartsData, dataType) {
      let labels = [];
      let data = {};
      if (dataKey instanceof Array) {
        dataKey.forEach((key) => {
          data[key] = [];
        });
      }
      else {
        data[dataKey] = [];
      }
      if (rows && rows.length > 0) {
        rows.forEach((row) => {
          if (row.created) {
            row.createdTime = moment(row.created).toLocaleString();
          }
          if (dataType == 'temp') {
            row[dataKey] = utils.prepareTempValue(row[dataKey]);
          }
          if (dataType == 'glucose') {
            row[dataKey] = utils.prepareSugarValue(row[dataKey]);
          }
          if (dataType == 'pose') {
              row[dataKey] = this.$t(utils.preparePoseName(row[dataKey]));
          }
          labels.push(chartsData.labelTranslations && chartsData.labelTranslations[row[labelKey]]
            ? chartsData.labelTranslations[row[labelKey]] : row[labelKey]);
          if (dataKey instanceof Array) {
            dataKey.forEach((key) => {
              data[key].push(row[key]);
            });
          }
          else {
            data[dataKey].push(row[dataKey]);
          }
        });
      }
      let finalData;
      if (dataKey instanceof Array) {
        finalData = [];
        for (let key in data) {
          finalData.push(data[key]);
        }
      }
      else {
        finalData = data[dataKey];
      }
      return {
        labels,
        datasets: [{
          data: finalData,
          backgroundColor: '#00ffe0',
          label: `Medical data (${dataType})`
        }],
        series: chartsData.series,
        chartsData
      };
    },
  },

  mounted() {
    this.selectedMedicalDataType = this.$store.state.selectedMedicalDataType;
    this.selectedPeriod = this.availablePeriods[0];
    this.loadHistoryData();
  },

  watch: {
    selectedPatient(newVal, oldVal) { // watch it
      this.loadHistoryData();
    }
  },
};
</script>

<style scoped>
  .selectors-block {
    display: flex;
  }
  .selectors-block > div {
    width: auto;
    min-width: 200px;
    margin-right: 20px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Chart.vue